<template>
	<div class="px-3 mt-4">
		<p class="flight-pack-text text-grey-900">{{ content.flight_select_pack }}</p>
		<div class="d-flex align-items-center gap-2">
			<div v-for="packObj in packagesToDisplay" :key="packObj.codePackage" @click="handlePackageChange(packObj)"
				class="package-tab d-flex align-items-center justify-content-between p-3   col" :class="packCode === packObj.codePackage ? 'active' : 'text-grey-900'
					">
				<span class="d-flex align-items-center">
					<template v-for="(component, componentIndex) in getPackageCompositionConfig(
						packObj.codePackage
					)" :key="`${packObj.codePackage}-${componentIndex}`">
						<span v-if="componentIndex > 0" class="mx-2 text-grey-900">+</span>
						<span class="d-flex align-items-center me-1 flex-column">
							<svg width="20" height="20" class="icon me-1">
								<use :xlink:href="`#___icon___${component.icon}`"></use>
							</svg>
							<span class="package-label">{{ getContentKey(component) }}</span>
						</span>
					</template>
				</span>

				<!-- Discount Badge -->
				<div class="d-flex justify-content-start align-items-center">
					<div v-if="
						packObj.codePackage !== 'HS' &&
						promos[packObj.codePackage] &&
						promos[packObj.codePackage] >= 5
					" class="d-flex align-items-center justify-content-center prestation-discount-container f bg-primary-orange ms-1 ms-xl-3">
						<div class="prestation-discount subcaption-strong text-primary-white px-2 py-1 text-nowrap">
							-{{ promos[packObj.codePackage] }}%
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { computed, onMounted } from "vue"
import { useStore } from "vuex"

// Computed
import packagesToDisplay from "@/vue/planning/computed/packagesToDisplay"

// Config
import packageCompositionConfig from "@/config/packageCompositionConfig"
import { type PackageComponent } from "@/config/packageCompositionConfig"

// Logic
import { isCS } from "@/vue/planning/logic/cs"
import getPackageComposition from "@/vue/planning/logic/getPackageComposition"

// Utils 
import { hasChangedTransportType } from "@/utils/transportation"
import { airportCodeMap } from "@/config/airportCodeMap"
import flightPackCode from "@/config/flightPackCode"
import transportPackCode from "@/config/transportPackCode"

const store = useStore()

// Computed
const packCode = computed<string>(() => store.state.packCode)
const isLocaleNL = computed<boolean>(() => store.getters.isLocaleNL)
const isLocaleEN = computed<boolean>(() => store.getters.isLocaleEN)
const content = computed<Record<string, string>>(() => store.state.content)
const availablePackCodes = computed<{ codePackage: string }[]>(() => store.getters.availablePackCodes)
const maxPromosFromAvailablePackages = computed(() => store.getters.maxPromosFromAvailablePackages)

// Methods
const getPackageCompositionConfig = (packageCode: string) => {
	const composition = packageCompositionConfig[packageCode]?.components || []
	return composition
}

const getContentKey = (component: PackageComponent) => {
	// Use short version for Dutch and English locales
	let keyToUse = component.contentKey
	if ((isLocaleNL.value || isLocaleEN.value) && component.contentKeyShort) {
		keyToUse = component.contentKeyShort
	}
	return content.value[keyToUse]
}

const handlePackageChange = async (packObj: {
	codePackage: string
	packageId: string
}) => {
	// Check if the new package is a flight pack and select the first available origin
	const isFlightPackage = flightPackCode.includes(packObj.codePackage)
	const isTrainPackage = transportPackCode.includes(packObj.codePackage)
	if (isFlightPackage) {
		// Always select the first available origin when switching to a flight package tab
		store.dispatch("selectDefaultFlightOrigin", { overwrite: true, forceFlightPackCode: true })
	} else if (isTrainPackage) {
		// Always select the default train origin when switching to a train package tab
		store.dispatch("selectDefaultTrainOrigin", { overwrite: true, forceTrainPackCode: true })
	}
	const changedTransportType: boolean = hasChangedTransportType(store.state.packCode, packObj.codePackage, store.state.transportationSelected)

	if (packCode.value === packObj.codePackage) {
		store.dispatch("setPackagesMobileDropdownOpen", false)
		return
	}

	// First update package composition if needed
	if (packObj.codePackage !== "HS" && !isCS(packObj)) {
		await getPackageComposition(store)
	}

	// Then update the package
	await store.dispatch("setPackage", packObj.codePackage)
	store.state.packageId = packObj.packageId

}


const promos = computed(() => {
	const promoObject: Record<string, number> = {}
	if (Array.isArray(availablePackCodes.value)) {
		availablePackCodes.value.forEach((packObj) => {
			promoObject[packObj.codePackage] =
				maxPromosFromAvailablePackages.value[packObj.codePackage]
		})
	}
	return promoObject
})


</script>

<style scoped>
.package-tab {
	height: 80px;
	border-radius: var(--border-radius-card);
	border: 1px solid var(--grey-300);
	cursor: pointer;
}

.package-tab.active {
	border: 1px solid var(--primary-blue);
}

.prestation-discount-container {
	border-radius: 37px;
}

.flight-pack-text {
	font-size: 14px;
}
</style>
